/**
 * Reset Password — `/reset-password/:token`
 *
 * After successful reset the backend issues a session cookie, so we redirect
 * straight to the dashboard.
 */
import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";

import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Reset failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Reset failed. Please try again.";
}

const passwordIssues = (pw) => {
  const list = [];
  if (pw.length < 8) list.push("at least 8 characters");
  if (!/[0-9]/.test(pw)) list.push("a number");
  if (!/[\W_]/.test(pw)) list.push("a symbol");
  return list;
};

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const pwIssues = passwordIssues(password);
  const mismatch = password && confirm && password !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pwIssues.length) {
      setErr("Password must include " + pwIssues.join(", ") + ".");
      return;
    }
    if (mismatch) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(
        "/auth/reset-password",
        { token, password },
        { withCredentials: true },
      );
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="border-b border-black min-h-[80vh]"
      data-testid="reset-page"
    >
      <section className="max-w-md mx-auto px-6 lg:px-8 py-16">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
          / Reset password
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] mb-2">
          Set a new password.
        </h1>
        <p className="text-[#475569] text-sm mb-8">
          Choose something you haven&apos;t used here before.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              New password
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Lock className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="At least 8 chars, 1 number, 1 symbol"
                data-testid="reset-password-input"
                autoComplete="new-password"
              />
            </div>
            {password && pwIssues.length > 0 && (
              <p className="text-[10px] text-[#B45309] mt-1.5">
                Add: {pwIssues.join(", ")}.
              </p>
            )}
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Confirm new password
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Lock className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="Repeat your password"
                data-testid="reset-confirm-input"
                autoComplete="new-password"
              />
            </div>
            {mismatch && (
              <p className="text-[10px] text-[#B91C1C] mt-1.5">
                Passwords do not match.
              </p>
            )}
          </label>

          {err && (
            <p
              className="text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2"
              data-testid="reset-error"
            >
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#0A0A0A] text-white py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#0047FF] transition-colors"
            data-testid="reset-submit-button"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Updating…" : "Set new password"}
          </button>

          <div className="text-xs pt-1 text-[#475569]">
            <Link to="/login" className="underline hover:text-[#0047FF]">
              Back to login
            </Link>
          </div>
        </form>
      </section>
    </main>
  );
}
